import { apiClient } from '../lib/apiClient';
import { environmentService } from './environmentService';
import { loggingService } from './loggingService';

export interface ErrorContext {
  userId?: string;
  orgId?: string;
  userEmail?: string;
  sessionId?: string;
  url?: string;
  route?: string;
  componentStack?: string;
  userAgent?: string;
  timestamp: string;
  environment: string;
}

export interface BreadcrumbItem {
  timestamp: string;
  category: string;
  message: string;
  data?: Record<string, unknown>;
}

class ErrorTrackingService {
  private breadcrumbs: BreadcrumbItem[] = [];
  private maxBreadcrumbs = 50;
  private userContext: Partial<ErrorContext> = {};

  setUserContext(context: Partial<ErrorContext>) {
    this.userContext = {
      ...this.userContext,
      ...context,
    };
  }

  addBreadcrumb(category: string, message: string, data?: Record<string, unknown>) {
    this.breadcrumbs.push({
      timestamp: new Date().toISOString(),
      category,
      message,
      data,
    });

    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs.shift();
    }

    loggingService.debug('breadcrumb', message, { category, ...data });
  }

  private getBreadcrumbs(): BreadcrumbItem[] {
    return [...this.breadcrumbs];
  }

  private getFullContext(): ErrorContext {
    return {
      ...this.userContext,
      sessionId: loggingService.getSessionId(),
      url: window.location.href,
      route: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      environment: environmentService.getEnvironment(),
    };
  }

  private sanitizeError(error: Error): Record<string, unknown> {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
      code: (error as any).code,
      type: error.constructor.name,
    };
  }

  async captureError(
    error: Error,
    context?: Partial<ErrorContext>,
    additionalData?: Record<string, unknown>
  ): Promise<void> {
    const fullContext = {
      ...this.getFullContext(),
      ...context,
    };

    const errorData = this.sanitizeError(error);
    const breadcrumbs = this.getBreadcrumbs();

    loggingService.error(
      'error_tracking',
      `Captured error: ${error.message}`,
      error,
      {
        ...additionalData,
        breadcrumbCount: breadcrumbs.length,
      },
      fullContext.userId,
      fullContext.orgId
    );

    try {
      await apiClient.post('/error-logs', {
        error_type: error.name,
        error_message: error.message,
        error_stack: error.stack || null,
        error_code: (error as any).code || null,
        user_id: fullContext.userId || null,
        org_id: fullContext.orgId || null,
        session_id: fullContext.sessionId || null,
        url: fullContext.url || null,
        route: fullContext.route || null,
        component_stack: fullContext.componentStack || null,
        user_agent: fullContext.userAgent || null,
        environment: fullContext.environment,
        breadcrumbs: breadcrumbs,
        context: {
          ...fullContext,
          ...additionalData,
        },
        error_details: errorData,
        created_at: fullContext.timestamp,
      });
    } catch (err) {
      console.error('Error tracking service failed:', err);
    }

    if (environmentService.isProduction() && this.isCriticalError(error)) {
      this.notifyCriticalError(error, fullContext);
    }
  }

  private isCriticalError(error: Error): boolean {
    const criticalPatterns = [
      /authentication/i,
      /authorization/i,
      /payment/i,
      /subscription/i,
      /database connection/i,
      /network error/i,
    ];

    return criticalPatterns.some(pattern => pattern.test(error.message));
  }

  private async notifyCriticalError(error: Error, context: ErrorContext) {
    console.error('[CRITICAL ERROR]', {
      error: error.message,
      context,
    });
  }

  async captureException(error: unknown, context?: Partial<ErrorContext>, additionalData?: Record<string, unknown>) {
    let errorObj: Error;

    if (error instanceof Error) {
      errorObj = error;
    } else if (typeof error === 'string') {
      errorObj = new Error(error);
    } else {
      errorObj = new Error('Unknown error occurred');
      additionalData = {
        ...additionalData,
        originalError: error,
      };
    }

    await this.captureError(errorObj, context, additionalData);
  }

  async captureMessage(
    level: 'info' | 'warning' | 'error',
    message: string,
    context?: Partial<ErrorContext>,
    additionalData?: Record<string, unknown>
  ) {
    const fullContext = {
      ...this.getFullContext(),
      ...context,
    };

    if (level === 'error') {
      loggingService.error('message_capture', message, undefined, additionalData, fullContext.userId, fullContext.orgId);
    } else if (level === 'warning') {
      loggingService.warning('message_capture', message, additionalData, fullContext.userId, fullContext.orgId);
    } else {
      loggingService.info('message_capture', message, additionalData, fullContext.userId, fullContext.orgId);
    }

    try {
      await apiClient.post('/error-logs', {
        error_type: 'message',
        error_message: message,
        error_stack: null,
        error_code: null,
        user_id: fullContext.userId || null,
        org_id: fullContext.orgId || null,
        session_id: fullContext.sessionId || null,
        url: fullContext.url || null,
        route: fullContext.route || null,
        user_agent: fullContext.userAgent || null,
        environment: fullContext.environment,
        breadcrumbs: this.getBreadcrumbs(),
        context: {
          ...fullContext,
          level,
          ...additionalData,
        },
        error_details: { level, message },
        created_at: fullContext.timestamp,
      });
    } catch (err) {
      console.error('Failed to capture message:', err);
    }
  }

  async getRecentErrors(limit = 50) {
    try {
      return await apiClient.get<any[]>(`/error-logs?limit=${limit}`);
    } catch (err) {
      console.error('Failed to fetch recent errors:', err);
      return [];
    }
  }

  async getErrorsByUser(userId: string, limit = 50) {
    try {
      return await apiClient.get<any[]>(`/error-logs?userId=${userId}&limit=${limit}`);
    } catch (err) {
      console.error('Failed to fetch user errors:', err);
      return [];
    }
  }

  async getErrorStats(orgId?: string) {
    try {
      const query = orgId ? `?orgId=${orgId}` : ''
      return await apiClient.get<any>(`/error-logs/stats${query}`);
    } catch (err) {
      console.error('Failed to fetch error stats:', err);
      return null;
    }
  }

  clearBreadcrumbs() {
    this.breadcrumbs = [];
  }
}

export const errorTrackingService = new ErrorTrackingService();
